import styled, { css } from 'styled-components';

import { Typography } from '@/components/Typography';
import type { HeaderThemeKeys } from '@/theme/types/components';

import { ReactComponent as Accessibility } from '@/assets/icons/accessibility-icon.svg';
import { ReactComponent as ArrowDown } from '@/assets/icons/arrowdown-icon.svg';
import { ReactComponent as ArrowUp } from '@/assets/icons/arrowup-icon.svg';
import { ReactComponent as Checkbox } from '@/assets/icons/checkbox-icon.svg';
import { ReactComponent as Close } from '@/assets/icons/close-icon.svg';
import { ReactComponent as Hamburger } from '@/assets/icons/hamburger-icon.svg';
import { ReactComponent as International } from '@/assets/icons/international-icon.svg';
import { ReactComponent as Phone } from '@/assets/icons/phone-icon.svg';
import { ReactComponent as Search } from '@/assets/icons/search-icon.svg';

export const DesktopNav = styled.ul<{
  $headerBackground: HeaderThemeKeys;
  $isMobileFooter: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  li {
    display: flex;
    align-items: center;
  }
  path {
    -webkit-transition: 0.2s;
    transition: 0.2s;
  }
  .selectOptions {
    position: relative;
  }

  ${({
    $headerBackground,
    $isMobileFooter,
    theme: {
      components: {
        header: { breakpoint: headerLayoutBreakpoint, theme: headerTheme },
      },
    },
  }) => css`
    .selectOptions:hover {
      cursor: pointer;
      color: ${headerTheme[$headerBackground].hover.headerInfoText.color};
      .arrowIcon path {
        stroke: ${headerTheme[$headerBackground].default.icon.color};
      }
      .language,
      .region {
        color: ${headerTheme[$headerBackground].hover.headerInfoText.color};
      }
      .internationalIcon path {
        stroke: ${headerTheme[$headerBackground].hover.headerInfoText.color};
      }
    }
    @media (max-width: ${headerLayoutBreakpoint}) {
      display: ${$isMobileFooter ? 'flex' : 'none'};
      ${$isMobileFooter &&
      css`
        width: 100%;
        justify-content: space-between;
        & > li:first-child {
          order: 2;
        }

        & > li:nth-of-type(2) {
          order: 1;
        }

        & > li:last-child {
          display: block;
          order: 3;
        }
      `}
    }
  `}
`;

export const MobileNav = styled.ul<{ $isMobileFooter: boolean }>`
  display: none;
  gap: 1rem;
  @media (max-width: 375px) {
    gap: 0.5rem;
  }

  path {
    -webkit-transition: 0.2s;
    transition: 0.2s;
  }

  ${({
    $isMobileFooter,
    theme: {
      components: {
        header: { breakpoint: headerLayoutBreakpoint },
      },
    },
  }) => css`
    @media (max-width: ${headerLayoutBreakpoint}) {
      display: ${$isMobileFooter ? 'none' : 'flex'};
    }
  `}
`;

export const MobileIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  line-height: 0;
  cursor: pointer;
  touch-action: manipulation;

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const Text = styled(Typography)`
  display: flex;
  color: #0e2240;
  font-family: '__openSans_025993', '__openSans_Fallback_025993';
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  -webkit-text-decoration: none;
  text-decoration: none;
  -webkit-transition: color 0.2s;
  transition: color 0.2s;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const InternationalIcon = styled(International)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  margin-top: -4px;

  ${({
    $headerBackground,
    $isShrunk,
    theme: {
      breakpoints,
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
          @media (max-width: ${breakpoints.md}) {
            path {
              stroke: white;
            }
          }
        `}
`;

export const AccessibilityIcon = styled(Accessibility)`
  ${({ theme }) => css`
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    margin-top: -4px;
    margin-right: ${theme.spacing(1)};
  `}
`;

export const PhoneIcon = styled(Phone)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  margin-top: -4px;
  ${({
    $headerBackground,
    $isShrunk,
    theme: {
      breakpoints,
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
          @media (max-width: ${breakpoints.md}) {
            path {
              stroke: white;
            }
          }
        `}
`;

export const SearchIcon = styled(Search)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    &:hover {
      path {
        fill: ${headerTheme[$headerBackground].hover.icon.color};
      }
    }
  `}
  ${({
    $headerBackground,
    $isShrunk,
    $textColorOption,
    theme: {
      components: {
        header: { breakpoint, theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            fill: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            fill: ${$textColorOption === 'light'
              ? 'white'
              : headerTheme[$headerBackground].default.icon.color};
          }
          ${$textColorOption === 'dark'
            ? css`
                @media (max-width: ${breakpoint}) {
                  path {
                    fill: ${headerTheme[$headerBackground].default.icon.color};
                  }
                }
              `
            : css`
                ${$textColorOption === 'dark'
                  ? css`
                      @media (max-width: ${breakpoint}) {
                        path {
                          fill: ${headerTheme[$headerBackground].default.icon
                            .color};
                        }
                      }
                    `
                  : css`
                      @media (max-width: ${breakpoint}) {
                        path {
                          fill: ${headerTheme[$headerBackground].default.icon
                            .color};
                        }
                      }
                    `}
              `}
        `}
`;

export const HamburgerIcon = styled(Hamburger)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  ${({
    $headerBackground,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) => css`
    &:hover {
      path {
        fill: ${headerTheme[$headerBackground].hover.icon.color};
      }
    }
  `}
  ${({
    $headerBackground,
    $isShrunk,
    $textColorOption,
    theme: {
      components: {
        header: { breakpoint, theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            stroke: ${$textColorOption === 'light'
              ? 'white'
              : headerTheme[$headerBackground].default.icon.color};
          }
          ${$textColorOption === 'dark'
            ? css`
                @media (max-width: ${breakpoint}) {
                  path {
                    stroke: ${headerTheme[$headerBackground].default.icon
                      .color};
                  }
                }
              `
            : css`
                ${$textColorOption === 'dark'
                  ? css`
                      @media (max-width: ${breakpoint}) {
                        path {
                          stroke: ${headerTheme[$headerBackground].default.icon
                            .color};
                        }
                      }
                    `
                  : css`
                      @media (max-width: ${breakpoint}) {
                        path {
                          stroke: ${headerTheme[$headerBackground].default.icon
                            .color};
                        }
                      }
                    `}
              `}
        `}
`;
export const CloseIcon = styled(Close)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 24px;
  height: 24px;
  ${({
    $headerBackground,
    $isShrunk,
    $textColorOption,
    theme: {
      components: {
        header: { breakpoint, theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            stroke: ${
              $textColorOption === 'light'
                ? 'white'
                : headerTheme[$headerBackground].default.icon.color
            }};
          }
          @media (max-width: ${breakpoint}) {
            path {
              stroke: white;
            }
          }
        `}
`;

export const ArrowUpIcon = styled(ArrowUp)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 18px;
  height: 20px;

  ${({
    $headerBackground,
    $isShrunk,
    $textColorOption,
    theme: {
      components: {
        header: { breakpoint, theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            stroke: ${$textColorOption === 'light'
              ? 'white'
              : headerTheme[$headerBackground].default.icon.color};
          }
          @media (max-width: ${breakpoint}) {
            path {
              stroke: white;
            }
          }
        `}
`;

export const ArrowDownIcon = styled(ArrowDown)<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $textColorOption?: string;
}>`
  width: 18px;
  height: 20px;

  ${({
    $headerBackground,
    $isShrunk,
    theme: {
      breakpoints,
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isShrunk
      ? css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
        `
      : css`
          path {
            stroke: ${headerTheme[$headerBackground].default.icon.color};
          }
          @media (max-width: ${breakpoints.md}) {
            path {
              stroke: white;
            }
          }
        `}
`;

export const CheckBoxIcon = styled(Checkbox)`
  width: 16px;
`;

export const FlexBox = styled.div`
  display: flex;
`;

export const LanguageBox = styled.div<{ $isMobileFooter: boolean }>`
  position: absolute;
  right: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  background: white;
  padding: 10px 0 12px 0;
  z-index: 99;
  width: 120px;
  li > div {
    width: 100%;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    padding: 7px 10px 6px 10px;
    justify-content: space-between;
  }
  li,
  li > div > p {
    color: #09091a;
  }

  ${({ $isMobileFooter, theme: { colors } }) => css`
    color: ${colors.black};
    li:hover {
      p {
        color: ${colors.primaryBlue};
        opacity: 0.5;
      }
    }
    ${$isMobileFooter ? 'bottom' : 'top'}: 30px;
  `}
`;

export const SelectLanguage = styled.div<{
  $headerBackground: HeaderThemeKeys;
  $isActive: boolean;
}>`
  display: flex;

  ${({
    $headerBackground,
    $isActive,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    $isActive &&
    css`
      color: ${headerTheme[$headerBackground].active.menu.color};
      .arrowIcon path {
        stroke: ${headerTheme[$headerBackground].active.menu.color};
      }
      .language {
        color: ${headerTheme[$headerBackground].active.menu.color};
      }
    `}
`;

export const LanguageItem = styled(Typography)<{
  $isActive: boolean;
}>`
  ${({ $isActive, theme: { colors } }) =>
    $isActive &&
    css`
      color: ${colors.primaryBlue} !important;
    `}
`;

export const Link = styled.a`
  text-decoration: none;
`;
